import { Box, Grid, Typography } from '@mui/material'
import usePractitionerReportInsights from './usePractitionerReportInsights'
import {
  DonutChart,
  INSIGHT_COLORS,
  InsightsCard,
  InsightsLoading,
  MetricTile,
  SectionEmpty,
  StatTileRow,
  TopKBarChart,
} from './insightsCharts'

function genderItems(gender) {
  if (!gender || typeof gender !== 'object') return []
  return Object.entries(gender)
    .filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => ({ key: k, count: Number(v) || 0 }))
}

function LensHeading({ children }) {
  return (
    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
      {children}
    </Typography>
  )
}

/**
 * Patient-cohort stats for the practitioner's own patients (across their reports in range):
 * new vs repeat, gender/age/location splits, scheduling, and care-team collaboration — which other
 * doctors/therapists/screeners see the same patients, and the top therapy delivered to them.
 */
export default function PatientInsights({ active, clinicBrandId, clinicLocationId, from, to }) {
  const { data, loading, error } = usePractitionerReportInsights({
    enabled: active,
    reportType: 'patient-stats',
    clinicBrandId,
    clinicLocationId,
    from,
    to,
  })

  if (loading && !data) return <InsightsLoading />
  if (error && !data) return <SectionEmpty label={error} />
  if (!data) return <SectionEmpty />

  const totals = data.totals || {}
  const gender = genderItems(data.gender)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <StatTileRow
        tiles={[
          { label: 'Patients', value: totals.patients, color: INSIGHT_COLORS[0] },
          { label: 'Sessions', value: totals.sessions, color: INSIGHT_COLORS[6] },
          { label: 'New patients', value: totals.newPatients, color: INSIGHT_COLORS[1] },
          { label: 'Repeat patients', value: totals.repeatPatients, color: INSIGHT_COLORS[2] },
        ]}
      />

      <LensHeading>Demographics</LensHeading>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <InsightsCard title="Gender split">
            <DonutChart items={gender} emptyLabel="No demographics on file" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InsightsCard title="Age groups">
            <TopKBarChart items={data.ageBuckets} color={INSIGHT_COLORS[3]} emptyLabel="No ages on file" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InsightsCard title="Top locations">
            <TopKBarChart items={data.topCities} color={INSIGHT_COLORS[4]} emptyLabel="No locations on file" />
          </InsightsCard>
        </Grid>
      </Grid>

      <InsightsCard title="Scheduling" subtitle="Actual wait time / duration aren't captured yet — scheduled length shown">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <MetricTile label="Avg scheduled session" value={data.avgScheduledSessionMins} suffix="min" />
        </Box>
      </InsightsCard>

      <LensHeading>Care team on my patients</LensHeading>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top consulting doctors">
            <TopKBarChart items={data.topConsultingDoctors} color={INSIGHT_COLORS[0]} emptyLabel="No other consulting doctors" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top therapists">
            <TopKBarChart items={data.topTherapists} color={INSIGHT_COLORS[2]} emptyLabel="No other therapists" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top screeners">
            <TopKBarChart items={data.topScreeners} color={INSIGHT_COLORS[5]} emptyLabel="No screeners" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top therapy on my patients">
            <TopKBarChart items={data.topTherapyOnPatients} color={INSIGHT_COLORS[1]} emptyLabel="No therapy recorded" />
          </InsightsCard>
        </Grid>
      </Grid>
    </Box>
  )
}
